import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { ChevronDown, Filter, X } from "lucide-react";
import type { GymOption, AppOption, UserFilters } from "@/types/superAdmin";
import { useTranslation } from "react-i18next";

interface AdvancedFiltersProps {
  filters: UserFilters;
  onFiltersChange: (filters: UserFilters) => void;
  gyms: GymOption[];
  apps: AppOption[];
  isLoading?: boolean;
}

const searchFieldIds = ["first_name", "last_name", "email", "phone", "address", "notes"] as const;
const subscriptionStatusValues = ["all", "active", "expired", "paused", "none"] as const;

export function AdvancedFilters({
  filters,
  onFiltersChange,
  gyms,
  apps,
  isLoading,
}: AdvancedFiltersProps) {
  const { t } = useTranslation(["superadmin", "common"]);
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);

  const searchFieldOptions = searchFieldIds.map((id) => ({
    id,
    label: t(`advancedFilters.fields.${id}`),
  }));

  const subscriptionStatusOptions = subscriptionStatusValues.map((value) => ({
    value,
    label: t(`advancedFilters.subscriptionStatuses.${value}`),
  }));

  const hasActiveFilters = Boolean(
    filters.gymId ||
    filters.appName ||
    filters.subscriptionStatus ||
    (filters.searchFields && filters.searchFields.length > 0)
  );

  const handleGymChange = (value: string) => {
    onFiltersChange({
      ...filters,
      gymId: value === "all" ? undefined : value,
    });
  };

  const handleAppChange = (value: string) => {
    onFiltersChange({
      ...filters,
      appName: value === "all" ? undefined : value,
    });
  };

  const handleSubscriptionStatusChange = (value: string) => {
    onFiltersChange({
      ...filters,
      subscriptionStatus: value === "all" ? undefined : value,
    });
  };

  const handleSearchFieldToggle = (fieldId: string, checked: boolean) => {
    const currentFields = filters.searchFields || [];
    const newFields = checked
      ? [...currentFields, fieldId]
      : currentFields.filter((f) => f !== fieldId);
    onFiltersChange({
      ...filters,
      searchFields: newFields.length > 0 ? newFields : undefined,
    });
  };

  const clearFilters = () => {
    onFiltersChange({
      ...filters,
      gymId: undefined,
      appName: undefined,
      subscriptionStatus: undefined,
      searchFields: undefined,
    });
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="flex items-center gap-2">
        <CollapsibleTrigger asChild>
          <Button
            variant="outline"
            size={isMobile ? "sm" : "default"}
            className={cn(
              "gap-2",
              hasActiveFilters && "border-primary text-primary"
            )}
          >
            <Filter className={cn(isMobile ? "h-3 w-3" : "h-4 w-4")} />
            <span>{t("advancedFilters.title")}</span>
            {hasActiveFilters && (
              <span className="bg-primary text-primary-foreground rounded-full px-1.5 text-xs">
                {[filters.gymId, filters.appName, filters.subscriptionStatus].filter(Boolean).length +
                  (filters.searchFields?.length || 0)}
              </span>
            )}
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform",
                isOpen && "rotate-180"
              )}
            />
          </Button>
        </CollapsibleTrigger>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4 mr-1" />
            {t("common:clear")}
          </Button>
        )}
      </div>

      <CollapsibleContent className="mt-4">
        <div className={cn(
          "p-4 rounded-lg border bg-card/50 space-y-4",
          isMobile && "p-3"
        )}>
          <div className={cn(
            "grid gap-4",
            isMobile ? "grid-cols-1" : "grid-cols-3"
          )}>
            {/* Gym Filter */}
            <div className="space-y-2">
              <Label className={cn(isMobile && "text-sm")}>{t("advancedFilters.gym")}</Label>
              <Select
                value={filters.gymId || "all"}
                onValueChange={handleGymChange}
                disabled={isLoading}
              >
                <SelectTrigger className={cn(isMobile && "text-sm")}>
                  <SelectValue placeholder={t("advancedFilters.allGyms")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("advancedFilters.allGyms")}</SelectItem>
                  {gyms.map((gym) => (
                    <SelectItem key={gym.id} value={gym.id}>
                      {gym.name} ({gym.user_count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* App Filter */}
            <div className="space-y-2">
              <Label className={cn(isMobile && "text-sm")}>{t("advancedFilters.application")}</Label>
              <Select
                value={filters.appName || "all"}
                onValueChange={handleAppChange}
                disabled={isLoading}
              >
                <SelectTrigger className={cn(isMobile && "text-sm")}>
                  <SelectValue placeholder={t("advancedFilters.allApps")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("advancedFilters.allApps")}</SelectItem>
                  {apps.map((app) => (
                    <SelectItem key={app.name} value={app.name}>
                      {app.name} ({app.user_count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Subscription Status Filter */}
            <div className="space-y-2">
              <Label className={cn(isMobile && "text-sm")}>{t("advancedFilters.subscriptionStatus")}</Label>
              <Select
                value={filters.subscriptionStatus || "all"}
                onValueChange={handleSubscriptionStatusChange}
                disabled={isLoading}
              >
                <SelectTrigger className={cn(isMobile && "text-sm")}>
                  <SelectValue placeholder={t("advancedFilters.subscriptionStatuses.all")} />
                </SelectTrigger>
                <SelectContent>
                  {subscriptionStatusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Search Fields */}
          <div className="space-y-2">
            <Label className={cn(isMobile && "text-sm")}>
              {t("advancedFilters.searchFieldsLabel")}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t("advancedFilters.searchFieldsDesc")}
            </p>
            <div className={cn(
              "flex flex-wrap gap-3 pt-2",
              isMobile && "gap-2"
            )}>
              {searchFieldOptions.map((field) => (
                <div key={field.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`field-${field.id}`}
                    checked={filters.searchFields?.includes(field.id) || false}
                    onCheckedChange={(checked) =>
                      handleSearchFieldToggle(field.id, checked as boolean)
                    }
                    disabled={isLoading}
                  />
                  <Label
                    htmlFor={`field-${field.id}`}
                    className={cn(
                      "text-sm cursor-pointer",
                      isMobile && "text-xs"
                    )}
                  >
                    {field.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
